import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Brand, SimNotice } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await login(username, password); nav("/"); } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between bg-[var(--navy)] p-14 relative overflow-hidden">
        <Brand light />
        <div className="relative z-10 max-w-md fade-up">
          <h1 className="text-5xl font-extrabold text-white leading-[1.05]">Banking and investing, built around you.</h1>
          <p className="mt-6 text-white/70 text-lg">Manage checking, cards, transfers and your portfolio from one secure dashboard.</p>
        </div>
        <div className="flex gap-2"><span className="h-1.5 w-16 bg-white rounded" /><span className="h-1.5 w-8 bg-[var(--red)] rounded" /></div>
        <div className="absolute -right-24 top-1/3 h-96 w-96 rounded-full border-[40px] border-[var(--blue)]/40" />
        <div className="absolute -right-10 bottom-10 h-40 w-40 rounded-full bg-[var(--red)]/80" />
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12 bg-white">
        <div className="lg:hidden mb-10"><Brand /></div>
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <h2 className="text-3xl font-bold text-[var(--navy)]">Welcome back</h2>
          <p className="text-slate-500 mt-1 mb-8">Sign in to your Maderian account.</p>
          <label className="text-sm font-semibold text-slate-700">Username</label>
          <div className="relative mt-1 mb-4">
            <User size={16} className="absolute left-3 top-3 text-slate-400" />
            <Input className="pl-9 h-11" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" data-testid="login-username-input" required />
          </div>
          <div className="flex justify-between items-center">
            <label className="text-sm font-semibold text-slate-700">Password</label>
            <Link to="/forgot-password" className="text-sm font-semibold text-[var(--blue)] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
          </div>
          <div className="relative mt-1 mb-6">
            <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
            <Input type="password" className="pl-9 h-11" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password-input" required />
          </div>
          {error && <div className="mb-4 text-sm text-[var(--red)] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[var(--blue)] hover:bg-[var(--navy)] text-base" data-testid="login-submit-button">
            {busy ? "Signing in..." : "Sign in"}
          </Button>
          <p className="mt-6 text-sm text-slate-500 text-center">New to Maderian? <Link to="/signup" className="font-semibold text-[var(--red)] hover:underline" data-testid="login-signup-link">Open an account</Link></p>
          <SimNotice className="mt-10" />
        </form>
      </div>
    </div>
  );
}
